/**
 * Node transformation utilities for converting DOM nodes to AST nodes
 */

import {
  AstNode,
  AstElement,
  AstText,
  TextRunLookup,
  TextRun,
  AstElementAttributes,
} from '../types';
import { parseInlineStyle } from './styles';
import { ATTRIBUTE_MAP, URI_ATTRIBUTES, VOID_ELEMENTS } from '../constants';
import { getBlockId, findBlockInfo } from './blockUtils';
import { getNonWhitespaceCount } from '../../../Highlighting/utils/highlightPositionUtils';

function isDangerousUri(val: unknown): boolean {
  return typeof val === 'string' && /^\s*(?:javascript|vbscript):/i.test(val);
}

/**
 * Gets all attributes from a DOM element and converts them to React props
 */
export function getElementAttributes(element: Element): AstElementAttributes {
  const sanitizedAttributes: AstElementAttributes = {};

  for (const attr of Array.from(element.attributes)) {
    const rawKey = attr.name;
    const value = attr.value;
    const key = rawKey.toLowerCase() as Lowercase<string>;
    // Drop inline event handlers entirely
    if (key.startsWith('on')) {
      continue;
    }

    // Map known attributes to React props
    const mapped = ATTRIBUTE_MAP[key] ?? rawKey;

    // Block javascript: URIs
    if (URI_ATTRIBUTES.has(key)) {
      // srcset can contain multiple candidates; check each item, not just the first URL.
      if (key === 'srcset' && typeof value === 'string') {
        const cleaned = value
          .split(',')
          .map((s) => s.trim())
          .filter((s) => !isDangerousUri(s))
          .join(', ');
        sanitizedAttributes[mapped] = cleaned;
        continue;
      }

      if (isDangerousUri(value)) {
        continue;
      }
    }

    if (mapped === 'style') {
      sanitizedAttributes.style = parseInlineStyle(value);
    } else {
      sanitizedAttributes[mapped] = value;
    }
  }

  return sanitizedAttributes;
}

/**
 * Transforms a text node to an AST text node
 * This is where we build the block index for highlight slicing
 */
export function transformTextNode(
  node: Node,
  path: number[],
  runId: number,
  textRunLookup: TextRunLookup,
): AstText | null {
  const content = node.textContent ?? '';

  // Skip empty text nodes
  if (!content) {
    return null;
  }

  const blockInfo = findBlockInfo(node);
  const nonWhitespaceLen = getNonWhitespaceCount(content);

  const astText: AstText = {
    type: 0,
    text: content,
    runId,
    blockId: blockInfo.id,
    parentBlockId: blockInfo.parentId,
    nonWsLen: nonWhitespaceLen,
    keyPath: path,
  };

  // Add to block index if this text belongs to a block
  // This is crucial for highlight slicing - we need to know all text runs in each block
  if (blockInfo.id !== null) {
    const existingRuns = textRunLookup.get(blockInfo.id) || [];

    // Calculate non-whitespace offsets within the block
    // Each text run's offset is cumulative from previous runs
    let nonWsStart = 0;
    if (existingRuns.length > 0) {
      const lastRun = existingRuns[existingRuns.length - 1];
      nonWsStart = lastRun.nonWsEnd;
    }

    const textRun: TextRun = {
      runId,
      blockId: blockInfo.id,
      parentBlockId: blockInfo.parentId,
      nonWsStart,
      nonWsEnd: nonWsStart + nonWhitespaceLen,
    };

    existingRuns.push(textRun);
    textRunLookup.set(blockInfo.id, existingRuns);
  }

  return astText;
}

/**
 * Processes a single child node and adds it to the children array
 */
export function processChildNode(
  child: Node,
  childPath: number[],
  runId: { current: number },
  textRunLookup: TextRunLookup,
): AstNode | null {
  if (child.nodeType === Node.TEXT_NODE) {
    return transformTextNode(child, childPath, runId.current++, textRunLookup);
  }

  if (child.nodeType === Node.ELEMENT_NODE) {
    return transformElementNode(
      child as Element,
      childPath,
      runId,
      textRunLookup,
    );
  }
  return null;
}

/**
 * Processes <d> element children directly (display: contents behavior)
 */
export function processDElementChildren(
  dElement: Element,
  childPath: number[],
  runId: { current: number },
  textRunLookup: TextRunLookup,
): AstNode[] {
  const children: AstNode[] = [];
  const dChildren = Array.from(dElement.childNodes);

  for (let i = 0; i < dChildren.length; i++) {
    const dChild = dChildren[i];
    const dChildPath = [...childPath, i];

    const childAst = processChildNode(dChild, dChildPath, runId, textRunLookup);
    if (childAst) {
      children.push(childAst);
    }
  }

  return children;
}

/**
 * Transforms an element node to an AST element node
 * Handles void elements, regular elements, and special <d> elements
 */
export function transformElementNode(
  element: Element,
  path: number[],
  runId: { current: number },
  textRunLookup: TextRunLookup,
): AstElement | null {
  const tagName = element.tagName.toLowerCase();
  const attributes = getElementAttributes(element);
  const blockId = getBlockId(element);
  const blockInfo = findBlockInfo(element);

  // Handle void elements (self-closing tags like <img>, <br>)
  if (VOID_ELEMENTS.has(tagName)) {
    return {
      type: 1,
      tag: tagName,
      attrs: Object.keys(attributes).length > 0 ? attributes : undefined,
      id: element.id || undefined,
      blockId,
      parentBlockId: blockInfo.parentId,
      children: [],
      keyPath: path,
    };
  }

  // Process children recursively
  const children: AstNode[] = [];
  const childNodes = Array.from(element.childNodes);

  for (let i = 0; i < childNodes.length; i++) {
    const child = childNodes[i];
    const childPath = [...path, i];

    if (
      child.nodeType === Node.ELEMENT_NODE &&
      (child as Element).nodeName.toLowerCase() === 'd'
    ) {
      // Handle <d> elements as display: contents (no visual container)
      const dChildren = processDElementChildren(
        child as Element,
        childPath,
        runId,
        textRunLookup,
      );
      children.push(...dChildren);
    } else {
      const childAst = processChildNode(child, childPath, runId, textRunLookup);
      if (childAst) {
        children.push(childAst);
      }
    }
  }

  return {
    type: 1,
    tag: tagName,
    attrs: Object.keys(attributes).length > 0 ? attributes : undefined,
    id: element.id || undefined,
    blockId,
    parentBlockId: blockInfo.parentId,
    children,
    keyPath: path,
  };
}
